/* global setTimeout, clearTimeout */
import React, {createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,} from 'react';
import {InteractionManager} from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import {getAccessToken} from './storage';
import {getSetting} from './persistentSettings';
import {uploadWorkOrderPhoto} from '../services/workOrderPhotoApi';
import {
    createLocalRecordId,
    createUnsyncedRecordError,
    isLocalOnlyRecordId,
    isServerRecordId,
} from './serviceRecordId';
import {buildVehicleEditPayload} from './workSheetVehicleEdit';
import {normalizeItemLines, toBackendItemLines} from './workSheetParts';

const resolveApiUrl = () => {
    return 'https://api.happysolutions.hu/api';
    // return 'http://localhost:8000/api';
};

const API_URL = resolveApiUrl();
if (__DEV__) {
    console.log('WorkSheetContext: Using API URL', API_URL);
}
const inFlightGetJsonRequests = new Map();

const STORAGE_KEYS = {
    worksheets: '@worksheets',
    customers:  '@service_customers',
    parts:      '@service_parts',
};

/* Backend route mapping (HappyPOS serviceModule).
   These are bounded "working set" pulls: screens that browse the full data
   (work-order list, customer list) page it server-side themselves
   (helpers/workOrderListApi.js, CustomerVehicleScreen) so a store with
   thousands of imported records never downloads them all. */
const EP = {
    worksheets:        '/serviceModule/work-orders',
    worksheet:         (id) => `/serviceModule/work-orders/${id}`,
    worksheetComments: (id) => `/serviceModule/work-orders/${id}/comments`,
    worksheetTimeEntries: (id) => `/serviceModule/work-orders/${id}/time-entries`,
    customersBase:     '/serviceModule/customers',
    customers:         '/serviceModule/customers?limit=100&page=1',
    customer:          (id) => `/serviceModule/customers/${id}`,
    parts:             '/serviceModule/item-catalog?limit=200&page=1',
    documentsUpload:   '/serviceModule/documents/upload',
    worksheetCompletionSummary: (id) => `/serviceModule/work-orders/${id}/completion-summary`,
    worksheetComplete: (id) => `/serviceModule/work-orders/${id}/complete`,
    aiCompletionSummary: '/ai/tools/completion-summary',
};

/** Newest work orders kept in memory for the dashboard / calendar / share picker. */
const WORKING_SET_LIMIT = 300;

/** Work orders persisted to AsyncStorage as the cold-start cache (newest first). */
const PERSISTED_WORKSHEET_CAP = 100;

/** Write-coalescing window for the cold-start cache. */
const PERSIST_DEBOUNCE_MS = 800;

const WorkSheetContext = createContext(null);

const generateId = () => {
    const year = new Date().getFullYear();
    const seq = String(Math.floor(Math.random() * 9000) + 1000);
    return `ML-${year}-${seq}`;
};

/* ────────── Normalizers ──────────
   Backend (HappyPOS serviceModule) returns work-orders with a different
   shape than these screens were designed around. Translate to the frontend
   model while keeping the original payload under `raw` for advanced screens.
*/

const STATUS_MAP = {
    open: 'pending',
    pending: 'pending',
    scheduled: 'pending',
    awaitingParts: 'pending',
    awaitingNorm: 'in_progress',
    inProgress: 'in_progress',
    in_progress: 'in_progress',
    inprogress: 'in_progress',
    workInProgress: 'in_progress',
    onHold: 'in_progress',
    completed: 'completed',
    done: 'completed',
    finished: 'completed',
    awaitingInvoice: 'completed',
    invoiced: 'invoiced',
    closed: 'invoiced',
};

const STATUS_TO_BACKEND_MAP = {
    pending: 'open',
    in_progress: 'inProgress',
    completed: 'awaitingInvoice',
    invoiced: 'invoiced',
};

const pickString = (...values) => {
    for (const v of values) {
        if (v === null || v === undefined) continue;
        const s = typeof v === 'string' ? v : String(v);
        if (s.trim()) return s.trim();
    }
    return '';
};

const formatCustomerName = (customer) => {
    if (!customer || typeof customer !== 'object') {
        return pickString(customer);
    }
    if (customer.companyName) return customer.companyName;
    const first = customer.name?.first || customer.firstName || '';
    const last = customer.name?.last || customer.lastName || '';
    const combined = `${first} ${last}`.trim();
    return combined || pickString(customer.fullName, customer.displayName, customer.email, customer.phone);
};

const buildWorkOrderPatchPayload = (ws = {}) => {
    const nextStatus = STATUS_TO_BACKEND_MAP[ws.status] || ws.rawStatus || ws.status;
    const payload = {
        ...buildVehicleEditPayload(ws),
        ...(nextStatus ? { status: nextStatus } : {}),
        ...(ws.jobSummary ? { description: ws.jobSummary, notes: ws.jobSummary } : {}),
        ...(ws.vin !== undefined ? { vin: ws.vin || '' } : {}),
        ...(pickString(ws.licensePlate, ws.plate) ? { licensePlate: pickString(ws.licensePlate, ws.plate) } : {}),
        ...(ws.priority ? { priority: ws.priority } : {}),
        ...(ws.finishDate !== undefined ? { finishDate: ws.finishDate || null } : {}),
        ...(ws.attributes && typeof ws.attributes === 'object' ? { attributes: ws.attributes } : {}),
        ...(Array.isArray(ws.tasks) ? { tasks: ws.tasks } : {}),
        ...(Array.isArray(ws.parts) ? { itemLines: toBackendItemLines(ws.parts) } : {}),
    };

    const numericKm = Number(ws.kilometers ?? ws.mileage);
    if (!Number.isNaN(numericKm)) {
        payload.kilometers = numericKm;
    }

    if (typeof ws.vehicleId === 'string' && ws.vehicleId.trim()) {
        payload.vehicleId = ws.vehicleId;
    }

    if (typeof ws.customerId === 'string' && ws.customerId.trim()) {
        payload.customerId = ws.customerId;
    }

    return payload;
};

export const normalizeWorkOrder = (wo) => {
    if (!wo || typeof wo !== 'object') return wo;
    // Already in frontend shape (has worksheetId + primitive customer string).
    if (wo.worksheetId && typeof wo.customer === 'string' && !wo.workOrderNumber) {
        return wo;
    }

    const vehicle = (typeof wo.vehicleId === 'object' && wo.vehicleId) ? wo.vehicleId : {};
    const vehicleAttrs = vehicle.attributes || {};
    const customer = (typeof wo.customerId === 'object' && wo.customerId) ? wo.customerId : {};

    const parts = normalizeItemLines(wo);
    const partsTotal = parts.reduce((sum, p) => sum + (p.qty * p.unitPrice), 0)
        || Number(wo?.costs?.salePrice || wo?.costs?.parts || wo?.financials?.grandTotal || 0);

    const car = pickString(
        [vehicle.brand, vehicle.model].filter(Boolean).join(' '),
        vehicle.model,
        vehicle.brand,
        vehicle.name,
    );

    const plate = pickString(
        wo.licensePlate,
        vehicleAttrs.plateNumber,
        vehicle.plateNumber,
    );

    const year = pickString(
        vehicleAttrs.year,
        vehicle.year,
        vehicleAttrs.firstRegistration?.slice?.(0, 4),
    );

    const jobSummary = pickString(
        wo.jobSummary,
        wo.notes,
        wo.description,
        Array.isArray(wo?.attributes?.serviceRequirements) ? wo.attributes.serviceRequirements.join(', ') : '',
    );

    return {
        ...wo,
        _id: wo._id || wo.id,
        worksheetId: wo.worksheetId || wo.workOrderNumber || wo.number || wo._id,
        customer: formatCustomerName(customer) || formatCustomerName(wo.customer),
        customerId: customer?._id || (typeof wo.customerId === 'string' ? wo.customerId : undefined),
        customerData: customer,
        car: car || 'N/A',
        year: year || vehicleAttrs.year || '',
        plate: plate || '—',
        vin: wo.vin || vehicle.serialNumber || vehicleAttrs.vin || '',
        // Extended vehicle fields from attributes or direct fields
        fuel: vehicleAttrs.fuel || vehicle.fuel || wo.fuel || '',
        color: vehicleAttrs.color || vehicle.color || wo.color || '',
        engineCode: vehicleAttrs.engineCode || vehicle.engineCode || wo.engineCode || '',
        displacement: vehicleAttrs.displacement || vehicle.displacement || wo.displacement || '',
        powerKw: vehicleAttrs.powerKw || vehicle.powerKw || wo.powerKw || '',
        powerHp: vehicleAttrs.powerHp || vehicle.powerHp || wo.powerHp || '',
        firstRegistration: vehicleAttrs.firstRegistration || vehicle.firstRegistration || wo.firstRegistration || '',
        motExpiry: vehicleAttrs.motExpiry || wo.attributes?.motExpiry || wo.motExpiry || '',
        kilometers: wo.kilometers || vehicle.mileage || 0,
        jobSummary,
        estimatedHours: Number(wo?.timeStats?.estimatedHours ?? wo?.estimatedHours ?? 0) || 0,
        // Server-recorded (persisted) time — combines every monitored + manual
        // entry the backend has for this work order. Surfaced so the detail
        // screen can show the authoritative saved total alongside the live timer.
        recordedWorkSeconds: Number(wo?.timeStats?.totalWorkTimeSeconds || 0) || 0,
        recordedBreakSeconds: Number(wo?.timeStats?.totalBreakTimeSeconds || 0) || 0,
        trackedTechnicians: Array.isArray(wo?.timeStats?.technicians) ? wo.timeStats.technicians : [],
        status: STATUS_MAP[wo.status] || wo.status || 'pending',
        rawStatus: wo.status,
        priority: wo.priority || 'normal',
        tasks: Array.isArray(wo.tasks) ? wo.tasks : [],
        parts,
        partsTotal,
        vehicleData: vehicle,
        vehicleId: vehicle?._id || (typeof wo.vehicleId === 'string' ? wo.vehicleId : undefined),
        scheduledAt: wo.scheduledAt || wo.startDate || null,
        dueDate: wo.dueDate || wo.finishDate || null,
        createdAt: wo.createdAt,
        updatedAt: wo.updatedAt,
        // Pass through raw attributes for service requirements display
        attributes: wo.attributes || {},
    };
};

const normalizeWorkOrders = (list) => (Array.isArray(list) ? list.map(normalizeWorkOrder) : []);

// Keep completed/invoice-ready records visible in service dashboards and history.
// Only hide records that are explicitly canceled/rejected/archived.
const TERMINAL_WORK_ORDER_STATUSES = new Set([
    'cancelled',
    'canceled',
    'cancel',
    'rejected',
    'archived',
]);

const normalizeStatusToken = (value) => pickString(value)
    .toLowerCase()
    .replace(/[^a-z]/g, '');

const isActiveWorkOrder = (workOrder = {}) => {
    const normalizedStatus = normalizeStatusToken(workOrder.status);
    const rawStatus = normalizeStatusToken(workOrder.rawStatus);

    return !TERMINAL_WORK_ORDER_STATUSES.has(normalizedStatus)
        && !TERMINAL_WORK_ORDER_STATUSES.has(rawStatus);
};

const filterActiveWorkOrders = (list = []) => list.filter(isActiveWorkOrder);

/* Normalize customer documents coming from HappyPOS serviceModule.
   The frontend screens (CustomerVehicleScreen, etc.) expect flat primitive
   fields (name, phone, email, address, isCompany, vehicles, …) while the
   backend returns a nested `name: { first, last }`, `contact: { phone, email }`
   and `address: { … }` shape. Keep `raw` for advanced screens. */

const formatAddress = (addr) => {
    if (!addr) return '';
    if (typeof addr === 'string') return addr;
    const parts = [
        addr.fullAddress,
        [addr.postCode || addr.zip, addr.city].filter(Boolean).join(' '),
        [addr.street, addr.streetType, addr.houseNumber].filter(Boolean).join(' '),
    ].filter(Boolean);
    return parts.join(', ');
};

export const normalizeCustomer = (c) => {
    if (!c || typeof c !== 'object') return c;
    // Already normalized (flat string name) → pass through but still coerce
    // defensively so downstream code never crashes on non-string names.
    if (typeof c.name === 'string' && Array.isArray(c.vehicles)) {
        return c;
    }

    const contact = c.contact || {};
    const address = c.address || c.addressData || {};
    const isCompany = !!(c.companyName || c.isCompany || c.customerType === 'company');
    const name = pickString(
        formatCustomerName(c),
        c.companyName,
        c.displayName,
        c.fullName,
        contact.email,
        contact.phone,
    ) || '—';

    return {
        ...c,
        _id: c._id || c.id,
        name,
        isCompany,
        phone: pickString(c.phone, contact.phone, contact.mobile),
        email: pickString(c.email, contact.email),
        address: pickString(c.address && typeof c.address === 'string' ? c.address : '', formatAddress(address)),
        taxId: pickString(c.taxId, c.taxNumber, c.vatNumber),
        companyName: pickString(c.companyName),
        vehicles: Array.isArray(c.vehicles) ? c.vehicles : [],
        raw: c,
    };
};

const normalizeCustomers = (list) => (Array.isArray(list) ? list.map(normalizeCustomer) : []);

/* ────────── API helpers ────────── */

const apiHeaders = (token) => ({
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`,
});

/**
 * Low-level authenticated fetch returning the raw `Response` (not JSON-parsed).
 * Exported so callers that must inspect the status code themselves — e.g. a
 * DELETE endpoint that answers `204 No Content` on success, where calling
 * `.json()` on the empty body would throw — can bypass {@link apiFetch}'s
 * automatic parsing. Still throws on a non-2xx status (`API {status}: {body}`).
 * @param {string} path request path (appended to `API_URL`).
 * @param {object} [options] fetch options (method, headers, body).
 * @returns {Promise<Response>} the raw fetch Response.
 */
export const apiRequest = async (path, options = {}) => {
    const token = await getAccessToken();
    if (!token) throw new Error('No auth token');

    const res = await fetch(`${API_URL}${path}`, {
        ...options,
        headers: { ...apiHeaders(token), ...options.headers },
    });

    if (!res.ok) {
        const errBody = await res.text().catch(() => '');

        // The MESSAGE FORMAT stays `API <status>: <body>` — serviceMode.js and
        // several screens match on it. The structured fields are additive, so a
        // screen can show the server's own sentence (see helpers/apiError.js)
        // instead of putting a raw JSON body in front of the user.
        const err = new Error(`API ${res.status}: ${errBody}`);

        err.status = res.status;
        err.body = errBody;

        try {
            err.payload = errBody ? JSON.parse(errBody) : null;
        } catch (_parseError) {
            err.payload = null;
        }

        // Both error conventions of the API: `{ message, code }` and `{ error, msg }`.
        err.code = err.payload?.code || err.payload?.msg || '';
        err.fields = Array.isArray(err.payload?.fields) ? err.payload.fields : [];

        throw err;
    }

    return res;
};

/* ────────── Short-lived GET response cache (stale-while-revalidate) ──────────
   Screens remount their lists on every tab visit; on a slow connection that is
   a spinner every time even when the data changed seconds ago. Every deduped
   GET therefore keeps its parsed JSON for a short TTL: a repeat read within the
   TTL renders instantly from memory while a background refresh updates the
   entry for the next reader. Memory-only ON PURPOSE — a user/store switch goes
   through restartApp, which drops this Map, so an entry can never outlive the
   session (or scope) that fetched it. Any non-GET through apiFetch clears the
   whole cache, so a write from THIS device is always visible to the next read.
   Payload sharing between callers is the transport's existing contract: the
   in-flight dedup below already hands the SAME parsed object to every
   concurrent caller — the cache only extends that window to the TTL. */
const getJsonResponseCache = new Map();

/** Default freshness window for cached GET payloads (ms). @type {number} */
const GET_CACHE_DEFAULT_TTL_MS = 10 * 1000;

/** Hard TTL ceiling so a caller-passed `cacheTtl` can never serve minutes-old data. @type {number} */
const GET_CACHE_MAX_TTL_MS = 60 * 1000;

/** Entry-count safety cap; oldest entries are evicted first. @type {number} */
const GET_CACHE_MAX_ENTRIES = 150;

/**
 * Live/order-style reads are NEVER served from this cache, mirroring the
 * backend's own HTTP-cache discipline (src/lib/redis/http-cache.js): a stale
 * order list is worse than a slow one. Matched against every `/`, `?`, `&`,
 * `=` separated segment of the path, so `/orders`, `/preOrders?page=1` and
 * `?type=live` are all excluded while `/work-orders` (hyphenated, no live
 * order flow) stays cacheable.
 * @type {Set<string>}
 */
const GET_CACHE_EXCLUDED_PATH_PARTS = new Set([
    'order', 'orders', 'archiveorder', 'preorder', 'preorders', 'pendingorders', 'live',
]);

/**
 * Whether a GET path may be cached at all (see GET_CACHE_EXCLUDED_PATH_PARTS).
 *
 * @param {string} path Request path as handed to {@link apiFetch}.
 * @returns {boolean} True when the path carries no live/order segment.
 */
const isCacheablePath = (path) => String(path)
    .toLowerCase()
    .split(/[/?&=]/)
    .every((part) => !GET_CACHE_EXCLUDED_PATH_PARTS.has(part));

/**
 * Drop every cached GET payload. Runs after any mutating request so local
 * writes are immediately visible; exported for flows that need an explicit
 * "everything must be fresh now" (e.g. after an out-of-band sync).
 *
 * @returns {void}
 */
export const clearApiGetCache = () => {
    getJsonResponseCache.clear();
};

/**
 * Store a parsed GET payload, evicting the oldest entries beyond the cap.
 *
 * @param {string} key Scope-qualified cache key.
 * @param {*} value Parsed JSON payload to keep.
 * @returns {void}
 */
const rememberGetPayload = (key, value) => {
    getJsonResponseCache.delete(key);
    getJsonResponseCache.set(key, { value, storedAt: Date.now() });

    while (getJsonResponseCache.size > GET_CACHE_MAX_ENTRIES) {
        getJsonResponseCache.delete(getJsonResponseCache.keys().next().value);
    }
};

/**
 * Authenticated JSON fetch used by every module riding this transport
 * (service, sales, management, HR, …).
 *
 * GET behaviour, in order:
 *  1. In-flight dedup — concurrent identical GETs share one request.
 *  2. Stale-while-revalidate cache — a repeat GET within `cacheTtl` (default
 *     10 s) resolves instantly with the last payload while a background
 *     refresh updates the entry for the next reader. Order/live paths are
 *     never cached, and any non-GET clears the whole cache. Cache keys
 *     include the `x-restaurant-id` scope header so two store scopes can
 *     never cross-serve each other's data.
 *
 * @param {string} path Request path appended to `API_URL`.
 * @param {object} [options] fetch options, plus the transport extras below.
 * @param {boolean} [options.skipDedup=false] Bypass the in-flight GET dedup.
 * @param {boolean} [options.skipCache=false] Bypass the GET response cache
 *   (pull-to-refresh style reads that must hit the network).
 * @param {number} [options.cacheTtl] Freshness window in ms; `0` disables
 *   caching for this call, values above 60 000 are clamped.
 * @returns {Promise<*>} Parsed JSON payload.
 */
export const apiFetch = async (path, options = {}) => {
    const requestOptions = { ...options };
    delete requestOptions.skipDedup;
    delete requestOptions.skipCache;
    delete requestOptions.cacheTtl;

    const method = String(options?.method || 'GET').toUpperCase();
    const isDedupableGet = method === 'GET' && !options?.body && options?.skipDedup !== true;

    if (!isDedupableGet) {
        const res = await apiRequest(path, requestOptions);

        if (method !== 'GET') {
            // Server state just changed — no cached GET can be trusted anymore.
            clearApiGetCache();
        }

        // PII redaction for demo mode happens globally at the transport level
        // (see app/demo/demoHttpProxy), so this response is already anonymised.
        return res.json();
    }

    // Scope-qualified identity: the same path answers different data under a
    // different store scope header, so the header is part of the key.
    const scopeId = options?.headers?.['x-restaurant-id'] || options?.headers?.['X-Restaurant-Id'] || '';
    const dedupKey = `${method}:${path}|${scopeId}`;

    const ttl = typeof options?.cacheTtl === 'number'
        ? Math.max(0, Math.min(options.cacheTtl, GET_CACHE_MAX_TTL_MS))
        : GET_CACHE_DEFAULT_TTL_MS;
    const useCache = ttl > 0 && options?.skipCache !== true && isCacheablePath(path);

    if (useCache) {
        const entry = getJsonResponseCache.get(dedupKey);

        if (entry && (Date.now() - entry.storedAt) <= ttl) {
            if (!inFlightGetJsonRequests.has(dedupKey)) {
                const revalidation = apiRequest(path, requestOptions)
                    .then((res) => res.json())
                    .then((fresh) => {
                        rememberGetPayload(dedupKey, fresh);

                        return fresh;
                    })
                    .finally(() => {
                        inFlightGetJsonRequests.delete(dedupKey);
                    });

                inFlightGetJsonRequests.set(dedupKey, revalidation);
                // Detached guard: when no later caller awaits this refresh, its
                // failure is intentionally swallowed — the reader that triggered
                // it already rendered from cache.
                revalidation.catch(() => {});
            }

            return entry.value;
        }
    }

    if (inFlightGetJsonRequests.has(dedupKey)) {
        return inFlightGetJsonRequests.get(dedupKey);
    }

    const requestPromise = apiRequest(path, requestOptions)
        .then((res) => res.json())
        .then((parsed) => {
            if (useCache) {
                rememberGetPayload(dedupKey, parsed);
            }

            return parsed;
        })
        .finally(() => {
            inFlightGetJsonRequests.delete(dedupKey);
        });

    inFlightGetJsonRequests.set(dedupKey, requestPromise);

    return requestPromise;
};

/**
 * Persist a completed time entry (monitored stopwatch or hand-entered) onto a
 * work order so its tracked time is stored server-side and folded into the work
 * order's combined time totals. Mirrors the backend addWorkOrderTimeEntry
 * contract. `technicianId` is omitted so the backend attributes the entry to the
 * authenticated user.
 *
 * @param {string} workOrderId  Mongo _id of the work order.
 * @param {{ type?: 'work'|'break'|'admin'|'travel', source?: 'tracked'|'manual',
 *   durationSeconds?: number, taskId?: string, startTime?: string,
 *   endTime?: string, description?: string }} entry
 * @returns {Promise<object|null>} the updated work order (+ created entry), or null.
 */
/**
 * Load everything the closing / hand-over sign-off sheet shows the signer:
 * customer, vehicle, tasks, parts, logged time, totals and the machine-key
 * warnings for whatever is still open.
 *
 * @param {string} workOrderId Work order id.
 * @returns {Promise<Object>} The completion-summary envelope.
 */
export const fetchWorkOrderCompletionSummary = async (workOrderId) => {
    if (isLocalOnlyRecordId(workOrderId)) {
        throw createUnsyncedRecordError(workOrderId);
    }

    return apiFetch(EP.worksheetCompletionSummary(workOrderId));
};

/**
 * Close a work order with the captured sign-off. Cancelling is NOT done here —
 * that keeps going through the normal status save.
 *
 * @param {string} workOrderId Work order id.
 * @param {Object} body        `{ status, summary, summarySource, note, signatures[] }`.
 * @returns {Promise<Object>} `{ workOrder, completion }`.
 */
export const completeWorkOrder = async (workOrderId, body = {}) => {
    if (isLocalOnlyRecordId(workOrderId)) {
        throw createUnsyncedRecordError(workOrderId);
    }

    return apiFetch(EP.worksheetComplete(workOrderId), {
        method: 'POST',
        body: JSON.stringify(body),
    });
};

/**
 * Ask the AI to draft the closing text from the server-derived summary. The
 * store's AI credits are charged by the backend; a 403 means the balance ran out.
 *
 * @param {Object} body `{ entityType, summary, tone?, locale? }`.
 * @returns {Promise<{headline: string, summary: string, highlights: string[], recommendations: string[]}>} The draft.
 */
export const requestAiCompletionSummary = async (body = {}) =>
    apiFetch(EP.aiCompletionSummary, {
        method: 'POST',
        body: JSON.stringify(body),
    });

export const logWorkOrderTime = async (workOrderId, entry = {}) => {
    if (!workOrderId) {
        return null;
    }

    if (isLocalOnlyRecordId(workOrderId)) {
        throw createUnsyncedRecordError(workOrderId);
    }

    return apiFetch(EP.worksheetTimeEntries(workOrderId), {
        method: 'POST',
        body: JSON.stringify(entry),
    });
};

/**
 * Fresh, THROWING read of one work order for its server-calculated time numbers
 * (`timers[]`, `timeStats`, `tasks[].trackedTimeMs`).
 *
 * Unlike {@link WorkSheetProvider}'s `fetchWorksheetById` this neither swallows a
 * failure (it would hand back the stale cached copy and the timer would show
 * numbers from before the last pause/stop) nor touches the shared worksheet list,
 * and it always bypasses the short GET cache.
 *
 * @param {string} workOrderId Mongo _id of the work order.
 * @returns {Promise<object|null>} The normalized work order, or null when none is given.
 * @throws {Error} The transport error, or an `UNSYNCED_RECORD` error for a local-only id.
 */
export const fetchWorkOrderFresh = async (workOrderId) => {
    if (!workOrderId) {
        return null;
    }

    if (isLocalOnlyRecordId(workOrderId)) {
        throw createUnsyncedRecordError(workOrderId);
    }

    const raw = await apiFetch(EP.worksheet(workOrderId), {skipCache: true});

    return raw ? normalizeWorkOrder(raw) : null;
};

/**
 * E-mail a work order (munkalap PDF + photos + documents) to its customer —
 * `POST /serviceModule/work-orders/:id/send-email`. Without a `to` override the backend uses the
 * linked service customer's address and answers 400 `NO_RECIPIENT` when there is none.
 * @param {string} workOrderId Work order `_id`.
 * @param {{to?: string, cc?: string[], subject?: string, message?: string}} [body] Overrides.
 * @returns {Promise<{sentTo: string, cc?: string[], warnings?: string[]}|null>}
 */
export const sendWorkOrderEmail = async (workOrderId, body = {}) => {
    if (!workOrderId) {
        return null;
    }

    if (isLocalOnlyRecordId(workOrderId)) {
        throw createUnsyncedRecordError(workOrderId);
    }

    return apiFetch(`/serviceModule/work-orders/${encodeURIComponent(workOrderId)}/send-email`, {
        method: 'POST',
        body: JSON.stringify(body),
    });
};

export const apiFetchBlob = async (path, options = {}) => {
    const res = await apiRequest(path, options);

    if (typeof res.blob === 'function') {
        return res.blob();
    }

    if (typeof res.arrayBuffer === 'function' && typeof Blob !== 'undefined') {
        const buffer = await res.arrayBuffer();
        return new Blob([buffer], {
            type: res.headers?.get?.('content-type') || 'application/octet-stream',
        });
    }

    throw new Error('Blob responses are not supported in this environment');
};

/*
 * Work-order photos.
 *
 * The previous helper here POSTed to `/service/worksheets/:id/photos`, a route
 * that does not exist on the backend (nothing ever called it either). Photos are
 * stored as Service Module documents linked to the work order — `services/
 * workOrderPhotoApi.js` owns that contract; re-exported so the context keeps its
 * `uploadPhoto` key, now pointing at a route that is really served.
 */
export const apiUploadPhoto = uploadWorkOrderPhoto;

/* ────────── Provider ────────── */

export const WorkSheetProvider = ({ children }) => {
    const mountedRef = useRef(true);
    useEffect(() => () => { mountedRef.current = false; }, []);

    /* Entities */
    const [worksheets, setWorksheets] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [parts, setParts] = useState([]);

    /* State flags */
    const [loaded, setLoaded] = useState(false);
    const [syncing, setSyncing] = useState(false);
    const [errors, setErrors] = useState({}); // { worksheets: '...', customers: '...', ... }

    /* Per-worksheet timeline cache */
    const [timelineByWs, setTimelineByWs] = useState({}); // { [wsId]: [events...] }

    const setError = useCallback((key, value) => {
        if (!mountedRef.current) return;
        setErrors(prev => {
            const next = { ...prev };
            if (value) next[key] = value; else delete next[key];
            return next;
        });
    }, []);

    const persistLocal = useCallback(async (storageKey, data) => {
        try {
            await AsyncStorage.setItem(storageKey, JSON.stringify(data));
        } catch (e) {
            console.warn(`WorkSheetContext: persist ${storageKey} failed`, e);
        }
    }, []);

    const loadLocal = useCallback(async (storageKey) => {
        try {
            const raw = await AsyncStorage.getItem(storageKey);
            return raw ? JSON.parse(raw) : null;
        } catch (e) {
            console.warn(`WorkSheetContext: load ${storageKey} failed`, e);
            return null;
        }
    }, []);

    /* The cold-start cache only needs the newest rows. Serialising + writing a
       store's whole work-order list (thousands of populated documents) on every
       save froze the JS thread, and it ran inside setState updaters. Cap it and
       coalesce bursts of writes into one. */
    const persistTimerRef = useRef(null);
    const pendingPersistRef = useRef(null);
    const persistWorksheets = useCallback((list) => {
        pendingPersistRef.current = list;
        if (persistTimerRef.current) return;
        persistTimerRef.current = setTimeout(() => {
            persistTimerRef.current = null;
            const next = pendingPersistRef.current;
            pendingPersistRef.current = null;
            if (Array.isArray(next)) {
                persistLocal(STORAGE_KEYS.worksheets, next.slice(0, PERSISTED_WORKSHEET_CAP));
            }
        }, PERSIST_DEBOUNCE_MS);
    }, [persistLocal]);

    useEffect(() => () => {
        if (persistTimerRef.current) clearTimeout(persistTimerRef.current);
    }, []);

    /* Bumped on every local create / update / delete so paged lists that hold
       their own copy of the data (WorkSheetListScreen) know to refresh. */
    const [worksheetsVersion, setWorksheetsVersion] = useState(0);
    const bumpWorksheetsVersion = useCallback(() => setWorksheetsVersion(v => v + 1), []);

    /* ────────── Worksheets ────────── */

    const fetchWorksheets = useCallback(async () => {
        if (!mountedRef.current) return;
        setSyncing(true);
        try {
            // Bounded "working set" (newest N) for the dashboard / calendar. The full
            // list screen pages the endpoint itself; an older backend that ignores
            // the paging params simply answers everything, as before.
            const data = await apiFetch(`${EP.worksheets}?limit=${WORKING_SET_LIMIT}&envelope=true`);
            const rawList = Array.isArray(data) ? data : (data?.worksheets || data?.workOrders || data?.docs || data?.data || []);
            const normalizedList = normalizeWorkOrders(rawList);
            const list = filterActiveWorkOrders(normalizedList);
            if (mountedRef.current) {
                setWorksheets(list);
                setError('worksheets', null);
                persistWorksheets(list);
            }
        } catch (err) {
            console.warn('WorkSheetContext: API fetch failed, using cache', err.message);
            setError('worksheets', err.message);
            const cached = await loadLocal(STORAGE_KEYS.worksheets);
            if (cached && mountedRef.current) {
                setWorksheets(normalizeWorkOrders(Array.isArray(cached) ? cached : []));
            }
        } finally {
            if (mountedRef.current) {
                setLoaded(true);
                setSyncing(false);
            }
        }
    }, [persistWorksheets, loadLocal, setError]);

    const saveWorksheet = useCallback(async (ws) => {
        // Only a real server id may be PATCHed. A work sheet created offline still
        // carries a local-only id, so it must be CREATED on the server (and adopt
        // the returned id) instead of patching an id the API cannot even cast.
        const isUpdate = isServerRecordId(ws._id);
        const wsToSave = {
            ...ws,
            _id: ws._id || createLocalRecordId(),
            worksheetId: ws.worksheetId || generateId(),
            createdAt: ws.createdAt || new Date().toISOString(),
            updatedAt: new Date().toISOString(),
        };

        setWorksheets(prev => {
            let updated;
            const idx = prev.findIndex(w => w._id === wsToSave._id);
            if (idx >= 0) { updated = [...prev]; updated[idx] = wsToSave; }
            else { updated = [wsToSave, ...prev]; }
            persistWorksheets(updated);
            return updated;
        });
        bumpWorksheetsVersion();

        try {
            if (isUpdate) {
                const patchPayload = buildWorkOrderPatchPayload(wsToSave);
                await apiFetch(EP.worksheet(wsToSave._id), {
                    method: 'PATCH',
                    body: JSON.stringify(patchPayload),
                });
            } else {
                // Never post the local id: the server mints its own `_id`, and a
                // client-minted one only fails validation on the way in.
                const {_id: localOnlyId, ...createPayload} = wsToSave;
                const created = await apiFetch(EP.worksheets, {
                    method: 'POST',
                    body: JSON.stringify(createPayload),
                });
                if (created?._id && created._id !== wsToSave._id) {
                    setWorksheets(prev => {
                        const mapped = prev.map(w => w._id === wsToSave._id ? { ...wsToSave, ...created } : w);
                        persistWorksheets(mapped);
                        return mapped;
                    });
                    bumpWorksheetsVersion();
                    return { ...wsToSave, ...created };
                }
            }
            setError('worksheets', null);
        } catch (err) {
            console.warn('WorkSheetContext: API save failed (cached locally)', err.message);
            setError('worksheets', err.message);
        }

        return wsToSave;
    }, [persistWorksheets, bumpWorksheetsVersion, setError]);

    /* Create work order via the composite endpoint (customer + vehicle + work order in one shot).
       Mirrors the admin's NewWorkOrderModal composite flow. */
    const createCompositeWorksheet = useCallback(async (payload) => {
        // payload = { workOrder: {...}, customer?: {...}, vehicle?: {...} }
        const compositeEP = `${EP.worksheets}/composite`;
        try {
            const response = await apiFetch(compositeEP, {
                method: 'POST',
                body: JSON.stringify(payload),
            });
            // Backend may wrap in { workOrder, customer, vehicle } or return the WO directly
            const wo = response?.workOrder || response?.data?.workOrder || response;
            const normalized = wo ? normalizeWorkOrder(wo) : null;
            if (normalized) {
                setWorksheets(prev => {
                    const idx = prev.findIndex(w => w._id === normalized._id);
                    let updated;
                    if (idx >= 0) { updated = [...prev]; updated[idx] = normalized; }
                    else { updated = [normalized, ...prev]; }
                    persistWorksheets(updated);
                    return updated;
                });
                bumpWorksheetsVersion();
            }
            setError('worksheets', null);
            return { ...response, normalized };
        } catch (err) {
            console.warn('WorkSheetContext: composite create failed', err.message);
            setError('worksheets', err.message);
            throw err;
        }
    }, [persistWorksheets, bumpWorksheetsVersion, setError]);

    const deleteWorksheet = useCallback(async (id) => {
        setWorksheets(prev => {
            const updated = prev.filter(w => w._id !== id);
            persistWorksheets(updated);
            return updated;
        });
        bumpWorksheetsVersion();

        // A work sheet that never reached the server has nothing to delete there.
        if (isLocalOnlyRecordId(id)) {
            return;
        }

        try {
            await apiFetch(EP.worksheet(id), { method: 'DELETE' });
        } catch (err) {
            console.warn('WorkSheetContext: API delete failed', err.message);
            setError('worksheets', err.message);
        }
    }, [persistWorksheets, bumpWorksheetsVersion, setError]);

    /* Latest list for stable-identity readers: getWorksheet / fetchWorksheetById
       used to depend on `worksheets`, so every list update re-created them and
       re-ran every effect that lists them as a dependency. */
    const worksheetsRef = useRef(worksheets);
    worksheetsRef.current = worksheets;

    const getWorksheet = useCallback(
        (id) => worksheetsRef.current.find(w => w._id === id) || null,
        []
    );

    const fetchWorksheetById = useCallback(async (id) => {
        // Local-only work sheets exist nowhere but in this device's cache.
        if (isLocalOnlyRecordId(id)) {
            return worksheetsRef.current.find(w => w._id === id) || null;
        }

        try {
            const raw = await apiFetch(EP.worksheet(id));
            const ws = raw ? normalizeWorkOrder(raw) : null;
            if (ws) {
                setWorksheets(prev => {
                    const idx = prev.findIndex(w => w._id === ws._id);
                    let updated;
                    if (idx >= 0) { updated = [...prev]; updated[idx] = ws; }
                    else { updated = [ws, ...prev]; }
                    persistWorksheets(updated);
                    return updated;
                });
                return ws;
            }
        } catch (err) {
            console.warn('WorkSheetContext: fetchById failed', err.message);
        }
        return worksheetsRef.current.find(w => w._id === id) || null;
    }, [persistWorksheets]);

    /* ────────── Customers & Vehicles ────────── */

    const fetchCustomers = useCallback(async () => {
        try {
            const data = await apiFetch(EP.customers);
            const rawList = Array.isArray(data) ? data : (data?.customers || data?.docs || []);
            const list = normalizeCustomers(rawList);
            if (mountedRef.current) {
                setCustomers(list);
                setError('customers', null);
                persistLocal(STORAGE_KEYS.customers, list);
            }
        } catch (err) {
            console.warn('WorkSheetContext: fetchCustomers failed', err.message);
            setError('customers', err.message);
            const cached = await loadLocal(STORAGE_KEYS.customers);
            if (cached && mountedRef.current) setCustomers(normalizeCustomers(Array.isArray(cached) ? cached : []));
        }
    }, [persistLocal, loadLocal, setError]);

    const saveCustomer = useCallback(async (customer) => {
        // Same contract as saveWorksheet: only a real server id may be PATCHed,
        // a customer captured offline has to be created first.
        const isUpdate = isServerRecordId(customer._id);
        const custToSave = normalizeCustomer({
            ...customer,
            _id: customer._id || createLocalRecordId(),
            vehicles: customer.vehicles || [],
            updatedAt: new Date().toISOString(),
        });

        setCustomers(prev => {
            const idx = prev.findIndex(c => c._id === custToSave._id);
            let updated;
            if (idx >= 0) { updated = [...prev]; updated[idx] = custToSave; }
            else { updated = [custToSave, ...prev]; }
            persistLocal(STORAGE_KEYS.customers, updated);
            return updated;
        });

        try {
            if (isUpdate) {
                await apiFetch(EP.customer(custToSave._id), {
                    method: 'PATCH', body: JSON.stringify(custToSave),
                });
            } else {
                // The server mints the `_id`; posting the local one only fails.
                const {_id: localOnlyCustomerId, ...createPayload} = custToSave;
                const created = await apiFetch(EP.customersBase, {
                    method: 'POST', body: JSON.stringify(createPayload),
                });
                if (created?._id && created._id !== custToSave._id) {
                    setCustomers(prev => {
                        const mapped = prev.map(c => c._id === custToSave._id ? { ...custToSave, ...created } : c);
                        persistLocal(STORAGE_KEYS.customers, mapped);
                        return mapped;
                    });
                    return { ...custToSave, ...created };
                }
            }
            setError('customers', null);
        } catch (err) {
            console.warn('WorkSheetContext: saveCustomer failed', err.message);
            setError('customers', err.message);
        }
        return custToSave;
    }, [persistLocal, setError]);

    const deleteCustomer = useCallback(async (id) => {
        setCustomers(prev => {
            const updated = prev.filter(c => c._id !== id);
            persistLocal(STORAGE_KEYS.customers, updated);
            return updated;
        });
        // A customer that never reached the server has nothing to delete there.
        if (isLocalOnlyRecordId(id)) {
            return;
        }

        try {
            await apiFetch(`/service/customers/${id}`, { method: 'DELETE' });
        } catch (err) {
            console.warn('WorkSheetContext: deleteCustomer failed', err.message);
            setError('customers', err.message);
        }
    }, [persistLocal, setError]);

    /* Vehicles are stored inside the customer document (customer.vehicles[]).
       These helpers keep the shape consistent and push the change. */

    const saveVehicle = useCallback(async (customerId, vehicle, customerHint = null) => {
        // `customerHint`: paged screens hold the customer themselves and it need
        // not be in the (bounded) context list.
        const current = customers.find(c => c._id === customerId) || customerHint;
        if (!current) throw new Error('Customer not found');

        const vehicles = current.vehicles || [];
        const vId = vehicle._id || Date.now().toString();
        const vToSave = { ...vehicle, _id: vId };
        const idx = vehicles.findIndex(v => v._id === vId);
        const nextVehicles = idx >= 0
            ? vehicles.map(v => v._id === vId ? vToSave : v)
            : [...vehicles, vToSave];

        return saveCustomer({ ...current, vehicles: nextVehicles });
    }, [customers, saveCustomer]);

    const deleteVehicle = useCallback(async (customerId, vehicleId, customerHint = null) => {
        const current = customers.find(c => c._id === customerId) || customerHint;
        if (!current) return;
        const nextVehicles = (current.vehicles || []).filter(v => v._id !== vehicleId);
        return saveCustomer({ ...current, vehicles: nextVehicles });
    }, [customers, saveCustomer]);

    /* ────────── Parts ────────── */

    const fetchParts = useCallback(async () => {
        try {
            const data = await apiFetch(EP.parts);
            const list = Array.isArray(data) ? data : (data?.parts || data?.items || data?.docs || []);
            if (mountedRef.current) {
                setParts(list);
                setError('parts', null);
                persistLocal(STORAGE_KEYS.parts, list);
            }
        } catch (err) {
            console.warn('WorkSheetContext: fetchParts failed', err.message);
            setError('parts', err.message);
            const cached = await loadLocal(STORAGE_KEYS.parts);
            if (cached && mountedRef.current) setParts(Array.isArray(cached) ? cached : []);
        }
    }, [persistLocal, loadLocal, setError]);

    const savePart = useCallback(async (part) => {
        const isUpdate = !!part._id;
        const partToSave = {
            ...part,
            _id: part._id || Date.now().toString(),
            updatedAt: new Date().toISOString(),
        };

        setParts(prev => {
            const idx = prev.findIndex(p => p._id === partToSave._id);
            let updated;
            if (idx >= 0) { updated = [...prev]; updated[idx] = partToSave; }
            else { updated = [partToSave, ...prev]; }
            persistLocal(STORAGE_KEYS.parts, updated);
            return updated;
        });

        // Parts (item-catalog) is read-only on the backend. Persist locally only.
        setError('parts', null);
        return partToSave;
    }, [persistLocal, setError]);

    const deletePart = useCallback(async (id) => {
        setParts(prev => {
            const updated = prev.filter(p => p._id !== id);
            persistLocal(STORAGE_KEYS.parts, updated);
            return updated;
        });
        // Parts are read-only on the backend; local delete only.
    }, [persistLocal]);

    /* ────────── Timeline ────────── */

    const timelineRef = useRef(timelineByWs);
    timelineRef.current = timelineByWs;

    const fetchTimeline = useCallback(async (worksheetId) => {
        if (!worksheetId) return [];
        // Nothing to read for a work sheet that has not been created server-side —
        // its timeline is whatever this device has collected locally.
        if (isLocalOnlyRecordId(worksheetId)) {
            return timelineRef.current[worksheetId] || [];
        }
        try {
            // Backend does not expose a dedicated timeline GET; derive from the
            // work-order document (comments/timeline are embedded).
            const ws = await apiFetch(EP.worksheet(worksheetId));
            const list = Array.isArray(ws?.timeline)
                ? ws.timeline
                : Array.isArray(ws?.comments)
                    ? ws.comments
                    : Array.isArray(ws?.events)
                        ? ws.events
                        : [];
            if (mountedRef.current) {
                setTimelineByWs(prev => ({ ...prev, [worksheetId]: list }));
            }
            return list;
        } catch (err) {
            // 404s are normal for brand-new worksheets
            if (!/404/.test(err.message)) {
                console.warn('WorkSheetContext: fetchTimeline failed', err.message);
            }
            return [];
        }
    }, []);

    const appendTimelineEvent = useCallback(async (worksheetId, event) => {
        if (!worksheetId) return;
        const payload = {
            ...event,
            at: event.at || new Date().toISOString(),
        };

        // optimistic local
        setTimelineByWs(prev => {
            const list = prev[worksheetId] || [];
            return { ...prev, [worksheetId]: [...list, { ...payload, _id: payload._id || Date.now().toString() }] };
        });

        // A local-only work sheet has no server document to comment on; the
        // optimistic entry above is the whole story until it is created.
        if (isLocalOnlyRecordId(worksheetId)) {
            return;
        }

        try {
            // Backend expects { text, type: 'internal'|'customer', author }
            const commentType = payload.type === 'customer' ? 'customer' : 'internal';
            const commentBody = {
                text: payload.text || payload.message || payload.note || payload.title || payload.type || 'event',
                type: commentType,
                author: payload.author || payload.user || payload.by || 'system',
            };
            const created = await apiFetch(EP.worksheetComments(worksheetId), {
                method: 'POST',
                body: JSON.stringify(commentBody),
            });
            if (created?._id) {
                setTimelineByWs(prev => {
                    const list = prev[worksheetId] || [];
                    return {
                        ...prev,
                        [worksheetId]: list.map(e =>
                            (!e._id || String(e._id).length <= 20) && e.type === payload.type && e.at === payload.at
                                ? { ...e, ...created } : e
                        ),
                    };
                });
            }
        } catch (err) {
            if (!/404/.test(err.message)) {
                console.warn('WorkSheetContext: appendTimelineEvent failed', err.message);
            }
        }
    }, []);

    /* ────────── Bookings (derived from worksheets.scheduledAt) ──────────
       If/when the backend exposes /service/bookings this can switch to a
       real endpoint without touching any screen. */
    const bookings = useMemo(() => {
        return (worksheets || [])
            .filter(ws => ws.scheduledAt || ws.dueDate)
            .map(ws => ({
                _id: ws._id,
                worksheetId: ws._id,
                worksheetNo: ws.worksheetId,
                title: ws.jobSummary || ws.worksheetId,
                customer: ws.customer,
                car: ws.car,
                plate: ws.plate,
                startAt: ws.scheduledAt || ws.dueDate,
                durationHours: ws.estimatedHours || 1,
                status: ws.status,
                worksheet: ws,
            }));
    }, [worksheets]);

    /* ────────── Lifecycle ────────── */

    const refreshAll = useCallback(async () => {
        await Promise.all([fetchWorksheets(), fetchCustomers(), fetchParts()]);
    }, [fetchWorksheets, fetchCustomers, fetchParts]);

    useEffect(() => {
        (async () => {
            // Preload the (capped) work-order cache for instant UI. Customers and
            // parts are NOT preloaded or pulled at boot any more: the only screens
            // that need them (customer list, parts inventory) fetch them on demand,
            // and the pickers search the server instead of scanning a local copy.
            const wsCache = await loadLocal(STORAGE_KEYS.worksheets);
            if (!mountedRef.current) return;
            if (Array.isArray(wsCache)) setWorksheets(normalizeWorkOrders(wsCache));
            setLoaded(true);

            // Network refresh is SERVICE-mode-only and OFF the boot critical path.
            // The provider stays mounted for every app mode so useWorkSheets never
            // throws for a cross-mode consumer, but a POS / warehouse / manager
            // session must not pay a service pull at cold start. Only a service
            // session auto-fetches, and only after interactions so it never
            // competes with first paint.
            const mode = await getSetting('userAppMode');
            if (mode === 'service' && mountedRef.current) {
                InteractionManager.runAfterInteractions(() => {
                    if (!mountedRef.current) return;
                    fetchWorksheets();
                });
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const value = useMemo(() => ({
        // Worksheets
        worksheets, loaded, syncing, errors, worksheetsVersion,
        saveWorksheet, createCompositeWorksheet, deleteWorksheet, getWorksheet,
        fetchWorksheets, fetchWorksheetById,
        // Customers
        customers, fetchCustomers, saveCustomer, deleteCustomer,
        // Vehicles
        saveVehicle, deleteVehicle,
        // Parts
        parts, fetchParts, savePart, deletePart,
        // Timeline
        timelineByWs, fetchTimeline, appendTimelineEvent,
        // Bookings (derived)
        bookings,
        // Photos
        uploadPhoto: apiUploadPhoto,
        // Global
        refreshAll,
    }), [
        worksheets, loaded, syncing, errors, worksheetsVersion,
        saveWorksheet, createCompositeWorksheet, deleteWorksheet, getWorksheet,
        fetchWorksheets, fetchWorksheetById,
        customers, fetchCustomers, saveCustomer, deleteCustomer,
        saveVehicle, deleteVehicle,
        parts, fetchParts, savePart, deletePart,
        timelineByWs, fetchTimeline, appendTimelineEvent,
        bookings, refreshAll,
    ]);

    return (
        <WorkSheetContext.Provider value={value}>
            {children}
        </WorkSheetContext.Provider>
    );
};

export const useWorkSheets = () => {
    const ctx = useContext(WorkSheetContext);
    if (!ctx) throw new Error('useWorkSheets must be used within WorkSheetProvider');
    return ctx;
};

export default WorkSheetContext;
